/**
 * "Skjul priser"-tilstand (à la store B2B-apps' nettopris-toggle): en
 * forhandler kan vise kataloget til sin slutkunde uden at afsløre sine
 * B2B-indkøbspriser. Gælder katalog/produkt/søgning — kurv, ordrer og
 * admin viser altid priser (dem står man ikke og viser frem).
 * Valget huskes pr. enhed i localStorage.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

const STORAGE_KEY = "lab3d-skjul-priser";

interface PriceVisibilityState {
  hidePrices: boolean;
  setHidePrices: (hide: boolean) => void;
}

const PriceVisibilityContext = createContext<PriceVisibilityState | undefined>(undefined);

export function PriceVisibilityProvider({ children }: { children: ReactNode }) {
  const [hidePrices, setHidden] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const setHidePrices = useCallback((hide: boolean) => {
    setHidden(hide);
    try {
      localStorage.setItem(STORAGE_KEY, hide ? "1" : "0");
    } catch {
      // localStorage kan fejle — tilstanden gælder stadig for sessionen.
    }
  }, []);

  const value = useMemo(() => ({ hidePrices, setHidePrices }), [hidePrices, setHidePrices]);
  return (
    <PriceVisibilityContext.Provider value={value}>{children}</PriceVisibilityContext.Provider>
  );
}

export function usePriceVisibility(): PriceVisibilityState {
  const ctx = useContext(PriceVisibilityContext);
  // Uden provider (fx i tests) vises priser som normalt.
  return ctx ?? { hidePrices: false, setHidePrices: () => {} };
}
